// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useTheme } from '@mui/system';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, Slide, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { getAllFoldersThunk, moveProjectToFolderThunk, unassignProjectThunk } from 'Features/folderList/folderListThunk';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import stylesDef from './styles';

interface IMoveProjectToFolderDialogProps {
  showDialog: boolean;
  projectId: string;
  projectFolderId: string;
  setShowDialog: (show: boolean) => void;
  onSuccess?: (project?: IProject, folderId?: string) => void;
}

const Transition = React.forwardRef<unknown, TransitionProps>((props, ref) => <Slide ref={ref} direction="up" {...props} />);

const MoveProjectToFolderDialog = ({ showDialog, projectId, projectFolderId, setShowDialog, onSuccess }: IMoveProjectToFolderDialogProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  // State Variables
  const { folderList } = useAppSelector((state) => state.folderList);
  const [loading, setLoading] = useState(false);
  const [getAllFoldersLoading, setGetAllFoldersLoading] = useState(false);

  // useEffect
  useEffect(() => {
    // Always refetch the complete folder list on open, rather than trusting whatever's already
    // in state - folderList can be non-empty but incomplete (e.g. left over from a search, which
    // only returns folders relevant to that search - see AUT-6376), so "already have some
    // folders" isn't a safe signal that we have all of them.
    if (showDialog) {
      getAllFolders();
    }
  }, [showDialog]);

  // Methods
  const getAllFolders = async () => {
    setGetAllFoldersLoading(true);
    // API call to get all folders
    const response = await dispatch(getAllFoldersThunk());

    if (response) {
      setGetAllFoldersLoading(false);
    }
  };

  const handleMoveProjectToFolder = async (folderId: string) => {
    // Validate folderId and projectId
    if (!folderId || !projectId) {
      return;
    }

    setLoading(true);
    // API call to move the project to the selected folder using folderId and projectId
    const response = await dispatch(moveProjectToFolderThunk(projectId, folderId));
    setLoading(false);
    if (response.success) {
      setShowDialog(false);
      onSuccess?.(response.data as IProject, folderId);
    }
  };

  const handleUnassignProject = async () => {
    setLoading(true);
    // API call to remove the project's folder assignment
    const response = await dispatch(unassignProjectThunk(projectId));
    setLoading(false);
    if (response.success) {
      setShowDialog(false);
      onSuccess?.(response.data as IProject, undefined);
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      TransitionComponent={Transition}
      keepMounted={false}
      sx={styles.dialog}
    >
      <Box sx={styles.titleSection}>
        <DialogTitle sx={styles.dialogTitle}>
          Move To...
        </DialogTitle>
        <IconButton
          color="inherit"
          onClick={() => setShowDialog(false)}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={styles.dialogText}>
          Please select the folder where you want to move this project to:
        </Typography>
        <Box sx={styles.folderListContainer}>
          {/* Get all folder loading */}
          { getAllFoldersLoading && (
            <Box sx={styles.loadingContainer}>
              <CircularProgress size={50} />
            </Box>
          )}

          {/* Unassign - only offered when the project is currently in a folder */}
          {!getAllFoldersLoading && projectFolderId && (
            <>
              <Box sx={styles.folderItem}>
                <Box sx={styles.folderDetails}>
                  <FolderOffOutlinedIcon sx={styles.folderIcon} />
                  <Typography variant="h6" sx={styles.folderName}>Unassigned</Typography>
                </Box>
                <Button
                  variant="text"
                  color="primary"
                  className="moveButton"
                  disabled={loading}
                  onClick={handleUnassignProject}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={styles.buttonProgress} />
                  ) : 'Unassign'}
                </Button>
              </Box>
              <Divider sx={styles.unassignDivider} />
            </>
          )}

          {/* Folders list */}
          {!getAllFoldersLoading && folderList?.length > 0 && folderList.map((folder) => (
            <Box key={folder._id} sx={styles.folderItem}>
              <Box sx={styles.folderDetails}>
                <FolderOutlinedIcon sx={styles.folderIcon} />
                <Typography variant="h6" sx={styles.folderName}>{folder.name}</Typography>
              </Box>
              <Button
                variant="text"
                color="primary"
                className="moveButton"
                disabled={loading || folder._id === projectFolderId}
                onClick={() => handleMoveProjectToFolder(folder._id)}
              >
                {loading ? (
                  <CircularProgress size={20} sx={styles.buttonProgress} />
                ) : 'Move'}
              </Button>
            </Box>
          ))}

          {/* No folder created */}
          {!getAllFoldersLoading && folderList?.length === 0 && (
            <Box sx={styles.noFoldersContainer}>
              <Box sx={styles.noFoldersIconContainer}>
                <FolderOutlinedIcon />
              </Box>
              <Typography variant="h6" sx={styles.noFoldersText}>
                No folders created yet.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MoveProjectToFolderDialog;
